<template>
    <div class="multi-select-input" :class="{'multi-select-input-focus': hasFocus}">
        <span v-for="item in items" :key="item.id" class="multi-select-input-value">
            <span>{{ item.name }}</span>
            <button
                type="button"
                class="multi-select-input-remove"
                tabindex="-1"
                @click="removeItem(item)">
                &times;
            </button>
        </span>

        <label class="multi-select-input-text" :title="placeholderLabel">
            <select
                id="select"
                v-model="model"
                class="multi-select-input-select"
                @keydown.backspace="handleBackspace($event)"
                @focus="gainFocus()"
                @blur="loseFocus()">
                <option v-if="isEmpty" :value="null">
                    {{ $t('common.placeholders.empty') }}
                </option>

                <template v-else-if="groupBy">
                    <option :value="null">
                        {{ placeholderLabel }}
                    </option>

                    <optgroup v-for="group in groups" :key="group.id" :label="group.name">
                        <option
                            v-for="option in group.options"
                            :key="option.id"
                            :value="option.id"
                            v-text="option.name" />
                    </optgroup>
                </template>

                <template v-else>
                    <option :value="null">
                        {{ placeholderLabel }}
                    </option>

                    <option
                        v-for="option in availableOptions"
                        :key="option.id"
                        :value="option.id"
                        v-text="option.name" />
                </template>
            </select>
        </label>
    </div>
</template>

<script>
import sortRecords from '@/js/helpers/sort-records';

export default {
    name: 'MultiSelectInput',

    inheritAttrs: false,

    model: {prop: 'items'},

    props: {
        items: {type: Array, default: () => []},
        options: {type: Array, required: true},
        groupBy: {type: String, default: null},
        placeholder: {type: String, default: null},
    },

    data() {
        return {
            hasFocus: false,
        };
    },

    computed: {
        model: {
            get() {
                return null;
            },

            set(id) {
                const item = this.options.find((item) => item.id === id);

                if (!item || this.items.some(({id}) => item.id === id)) {
                    return;
                }

                this.$emit('input', [...this.items, item]);
            },
        },

        placeholderLabel() {
            return this.placeholder || this.$t('common.placeholders.choose');
        },

        isEmpty() {
            return this.availableOptions.length === 0;
        },

        availableOptions() {
            return this.options
                .filter((option) => !this.items.some(({id}) => id === option.id))
                .sort(sortRecords);
        },

        groups() {
            if (!this.groupBy) {
                return [];
            }

            const groups = this.availableOptions
                .reduce((groups, option) => {
                    const group = option[this.groupBy];

                    if (!groups[group.id]) {
                        groups[group.id] = {...group, options: []};
                    }

                    groups[group.id].options.push(option);

                    return groups;
                }, {});

            return Object.values(groups).sort(sortRecords);
        },
    },

    methods: {
        gainFocus() {
            this.hasFocus = true;
        },

        loseFocus() {
            this.hasFocus = false;
        },

        removeItem(item) {
            this.$emit('input', this.items.filter(({id}) => id !== item.id));
        },

        removeLastItem() {
            if (this.items.length === 0) {
                return;
            }

            this.removeItem(this.items[this.items.length - 1]);
        },

        handleBackspace(event) {
            event.preventDefault();
            this.removeLastItem();
        },
    },
};
</script>

<style scoped>
.has-error /deep/ .multi-select-input {
    border-color: #dc3545;
}

.has-error /deep/ .multi-select-input.multi-select-input-focus {
    border-color: #dc3545;

    -webkit-box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
    box-shadow: 0 0 0 0.2rem rgba(220, 53, 69, 0.25);
}
</style>

<style lang="scss" scoped>
.multi-select-input {
    display: flex;
    flex-wrap: wrap;

    border: 1px solid #ced4da;
    border-radius: 0.25rem;

    background-color: #fff;
    padding: 0.375rem 0.75rem 0 0.75rem;
    background-clip: padding-box;

    font-size: 0.9rem;
    line-height: 1.6;

    &.multi-select-input-focus {
        border-color: #80bdff;

        outline: 0;

        -webkit-box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
        box-shadow: 0 0 0 0.2rem rgba(0, 123, 255, 0.25);
    }

    .multi-select-input-value {
        display: inline-flex;
        line-height: 1.6;
        align-items: center;
        font-size: 0.889em;
        background-color: #41B883;
        color: #fff;
        border-radius: 0.25rem;
        user-select: none;
        padding: 0 0.25rem;
        margin-right: 0.5rem;
        margin-bottom: 0.375rem;

        &:last-of-type {
            margin-right: 0;
        }

        .multi-select-input-remove {
            color: #266d4d;
            font-size: 1.125em;
            line-height: 1.6;
            padding: 0;
            margin-left: 0.25rem;
            border: none;
            background-color: #41B883;
            cursor: pointer;

            &:first-child {
                margin-right: .25rem;
            }

            &:last-child {
                margin-left: .25rem;
            }

            &:hover {
                color: darkred;
            }

            &:focus {
                outline: 0;
            }
        }
    }

    .multi-select-input-text {
        flex: 1;
        outline: 0;

        position: relative;

        min-width: 10rem;

        padding: 0;
        margin-left: 0.5rem;
        margin-bottom: 0.375rem;

        overflow: visible;
        border: none;
        background-color: white;
        color: #495057;

        line-height: 1.6;

        pointer-events: none;
        user-select: none;

        &::before {
            content: attr(title);

            color: inherit;
            opacity: .5; // #dc3545
        }

        .multi-select-input-select {
            position: absolute;
            top: 0;
            right: 0;
            bottom: 0;
            left: 0;
            width: 100%;
            opacity: 0.01;
            pointer-events: all;
            user-select: all;
        }
    }
}
</style>
